import { useEffect, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useNotify } from '../../../components/notifications';
import { fetchTatOptions, type TatOption } from './detailApi';
import type { QuoteAnalysisRow, SampleMatrixOption } from './detailTypes';
import './quotes.css';

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/**
 * The TAT cell picker — legacy <c>TurnAroundTime_ListView_Quotes</c> (700 × 600):
 * only the turnarounds named in the test's own price-list rows, the row's current
 * TAT pre-selected, single choice. Picking one re-prices the row on the server.
 */
export function TatPickerDialog({
  row, onPick, onClose,
}: {
  row: QuoteAnalysisRow | null;
  onPick: (tatId: string) => void;
  onClose: () => void;
}) {
  const notify = useNotify();
  const [options, setOptions] = useState<TatOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  useEffect(() => {
    if (!row?.testId) return;
    let cancelled = false;
    setLoading(true);
    setOptions([]);
    void fetchTatOptions(row.testId, row.isGroup, row.componentId)
      .then((list) => {
        if (cancelled) return;
        setOptions(list);
        setSelected(new Set(row.tatId ? [row.tatId.toLowerCase()] : []));
      })
      .catch((e) => { if (!cancelled) notify.error(e instanceof Error ? e.message : 'Failed to load the TATs.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [row, notify]);

  if (!row) return null;

  const columns: AppColumn<TatOption>[] = [
    { field: 'tat', header: 'TAT', width: 160 },
    { field: 'priority', header: 'Priority', width: 140 },
    { field: 'priceCode', header: 'PriceCode', width: 140 },
    { field: 'price', header: 'Price', width: 110, align: 'right', type: 'number', render: (o) => money.format(o.price) },
  ];

  function accept() {
    const id = [...selected][0];
    if (id === undefined) { notify.info('Select atleast one TAT.'); return; }
    onPick(String(id));
  }

  return (
    <AppDialog
      open
      title="TAT"
      width={700}
      className="dlg-center qt-picker"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" onClick={accept} disabled={loading}>OK</button>
          <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
        </>
      }
    >
      <AppDataGrid<TatOption>
        rows={options}
        columns={columns}
        getRowId={(o) => o.tatId.toLowerCase()}
        loading={loading}
        selectable
        selectedIds={selected}
        // Single choice, as legacy's popup: a new tick replaces the old one.
        onSelectionChange={(next) => {
          const added = [...next].find((k) => !selected.has(k));
          setSelected(added !== undefined ? new Set([added]) : next.size ? new Set([[...next][0]!]) : new Set());
        }}
        onRowDoubleClick={(o) => onPick(o.tatId)}
        hideHeaderTools
        emptyText="This test has no turnaround on its price list."
      />
    </AppDialog>
  );
}

/**
 * The Sample Matrix cell picker — legacy <c>VisualMatrix_ListView_Quotes</c>
 * ("Sample Matrices", 920 × 600): the sample matrices of the row's own Matrix,
 * the current one pre-selected, single choice.
 */
export function SampleMatrixPickerDialog({
  row, sampleMatrices, onPick, onClose,
}: {
  row: QuoteAnalysisRow | null;
  sampleMatrices: SampleMatrixOption[];
  onPick: (sampleMatrixId: string) => void;
  onClose: () => void;
}) {
  const notify = useNotify();
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  useEffect(() => {
    setSelected(new Set(row?.sampleMatrixId ? [row.sampleMatrixId.toLowerCase()] : []));
  }, [row]);

  if (!row) return null;
  const options = sampleMatrices.filter((m) => (m.matrix ?? '') === (row.matrix ?? ''));

  const columns: AppColumn<SampleMatrixOption>[] = [
    { field: 'name', header: 'Sample Matrix', width: 260 },
    { field: 'matrix', header: 'Matrix', width: 200 },
  ];

  function accept() {
    const id = [...selected][0];
    // Legacy wording, verbatim (:9415).
    if (id === undefined) { notify.error('Select atleast one sample matrix.'); return; }
    onPick(String(id));
  }

  return (
    <AppDialog
      open
      title="Sample Matrices"
      width={720}
      className="dlg-center qt-picker"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" onClick={accept}>OK</button>
          <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
        </>
      }
    >
      <AppDataGrid<SampleMatrixOption>
        rows={options}
        columns={columns}
        getRowId={(m) => m.id.toLowerCase()}
        selectable
        selectedIds={selected}
        onSelectionChange={(next) => {
          const added = [...next].find((k) => !selected.has(k));
          setSelected(added !== undefined ? new Set([added]) : next.size ? new Set([[...next][0]!]) : new Set());
        }}
        onRowDoubleClick={(m) => onPick(m.id)}
        hideHeaderTools
        emptyText="No other sample matrix for this matrix."
      />
    </AppDialog>
  );
}
